import { DateTime } from 'luxon';
import { create, type StoreApi, type UseBoundStore } from 'zustand';

import type { TransferSource } from 'app/(trade)/components/TransferBalance/queries';
import type { WithdrawalLimitItem } from 'utils/payments';
import { trackSprigEvent } from 'utils/sprig';
import { SPRIG_COMPLETED_WITHDRAWAL_FLOW_EVENT_NAME } from 'utils/sprig-constants';

import { FundMethods } from './registrationStore';

type WithdrawalStep = 'selection' | 'prereview' | 'card' | 'mfa' | 'initiated' | 'failed' | 'billing-address';

/**
 * The one destination a withdrawal is bound to. Several rails carry their own
 * identifier (`bankId`, `cardId`, `paymentId`, `cryptoWalletId`,
 * `stripeCardPayoutMethodId`) and `submit()` picks a branch from them, so two
 * identifiers being set at once means the money can leave for a destination
 * other than the one on the review screen. Selecting a destination therefore
 * goes through `selectDestination`, which writes the chosen identifier and
 * clears every competing one in a single update.
 */
export type WithdrawalDestination =
    | { rail: 'bank'; bankId: string }
    | { rail: 'savedCard'; cardId: string }
    /** Checkout.com card not yet added — the hosted card flow runs from review. */
    | { rail: 'newCheckoutCard' }
    /** Returning from the Checkout.com hosted flow with an authorized session. */
    | { rail: 'checkoutPayment'; paymentId: string }
    | { rail: 'stripeCard'; payoutMethodId: string }
    /** New card authorized through the Apple Pay sheet. */
    | { rail: 'applePaySheet' }
    | { rail: 'crypto'; cryptoWalletId: string }
    | { rail: 'braintree'; braintreeType: 'paypal' | 'venmo' };

type DestinationSlice = Pick<
    WithdrawalDialogState,
    | 'fundMethod'
    | 'bankId'
    | 'cardId'
    | 'paymentId'
    | 'cryptoWalletId'
    | 'stripeCardPayoutMethodId'
    | 'isStripeWithdrawal'
    | 'isApplePayWithdrawal'
    | 'braintreeType'
> & { stripeAccountLinked?: boolean };

const toDestinationSlice = (destination: WithdrawalDestination): DestinationSlice => {
    const cleared = {
        bankId: '',
        cardId: undefined,
        paymentId: undefined,
        cryptoWalletId: undefined,
        stripeCardPayoutMethodId: '',
        isStripeWithdrawal: false,
        isApplePayWithdrawal: false,
        braintreeType: undefined,
    };

    switch (destination.rail) {
        case 'bank':
            return { ...cleared, fundMethod: FundMethods.BANK, bankId: destination.bankId };
        case 'savedCard':
            return { ...cleared, fundMethod: FundMethods.DEBIT_CARD, cardId: destination.cardId };
        case 'newCheckoutCard':
            return { ...cleared, fundMethod: FundMethods.DEBIT_CARD };
        case 'checkoutPayment':
            return { ...cleared, fundMethod: FundMethods.DEBIT_CARD, paymentId: destination.paymentId };
        case 'stripeCard':
            return {
                ...cleared,
                fundMethod: FundMethods.DEBIT_CARD,
                stripeCardPayoutMethodId: destination.payoutMethodId,
                isStripeWithdrawal: true,
                // The recipient account is created server-side during payout
                // method registration; no hosted onboarding step.
                stripeAccountLinked: true,
            };
        case 'applePaySheet':
            return {
                ...cleared,
                fundMethod: FundMethods.DEBIT_CARD,
                isStripeWithdrawal: true,
                isApplePayWithdrawal: true,
                stripeAccountLinked: true,
            };
        case 'crypto':
            return { ...cleared, fundMethod: FundMethods.CRYPTO, cryptoWalletId: destination.cryptoWalletId };
        case 'braintree':
            return {
                ...cleared,
                fundMethod:
                    destination.braintreeType === 'paypal' ? FundMethods.BRAINTREE_PAYPAL : FundMethods.BRAINTREE_VENMO,
                braintreeType: destination.braintreeType,
            };
    }
};

interface WithdrawalDialogState {
    step: WithdrawalStep;
    fundMethod: FundMethods | undefined;
    lastInitiatedTime: DateTime;
    loading: boolean;
    errorTitle: string;
    errorMessage: string;
    amount: number;
    cardId: string | undefined;
    bankId: string;
    paymentId: string | undefined;
    cryptoWalletId: string | undefined;
    isStripeWithdrawal: boolean;
    stripeAccountLinked: boolean;
    stripeCardPayoutMethodId: string;
    // True while the "new card via Apple Pay sheet" flow is selected; a linked
    // apple_pay payout method instead rides the pinned-method path with
    // stripeCardPayoutMethodId set and this false.
    isApplePayWithdrawal: boolean;
    braintreeType: 'paypal' | 'venmo' | undefined;
    lockedLimitsDialogOpen: boolean;
    lockedLimitsDialogTitle: string;
    lockedLimitsDialogLockedLimits: WithdrawalLimitItem[];
    meshNetworkSelectionDialogOpen: boolean;
    meshAmountDialogOpen: boolean;
    meshAmountCents: number;
    meshWithdrawalSource: TransferSource;
    isMeshMfaStep: boolean;
    meshMethodSelectionDialogOpen: boolean;
    meshManualAddressEntryDialogOpen: boolean;
    // Set when the initiated step was reached via polling timeout rather than a
    // confirmed 'applied' status — the success screen must not claim completion.
    initiatedTimedOut: boolean;
    initiatedExpectedArrivalTs: string | undefined;
    initiatedTransferId: string | undefined;
    actions: {
        setAmount: (amount: number) => void;
        /** Binds the withdrawal to one destination, clearing all the others. */
        selectDestination: (destination: WithdrawalDestination) => void;
        setCardId: (cardId: string) => void;
        setBankId: (bankId: string) => void;
        setPaymentId: (paymentId: string) => void;
        setCryptoWalletId: (cryptoWalletId: string) => void;
        setIsStripeWithdrawal: (isStripeWithdrawal: boolean) => void;
        setStripeAccountLinked: (linked: boolean) => void;
        setStripeCardPayoutMethodId: (payoutMethodId: string) => void;
        setIsApplePayWithdrawal: (isApplePayWithdrawal: boolean) => void;
        setBraintreeType: (type: 'paypal' | 'venmo' | undefined) => void;
        setLockedLimitsDialogOpen: (open: boolean) => void;
        setLockedLimitsDialogTitle: (title: string) => void;
        setLockedLimitsDialogLockedLimits: (lockedLimits: WithdrawalLimitItem[]) => void;
        setMeshNetworkSelectionDialogOpen: (open: boolean) => void;
        setMeshAmountDialogOpen: (open: boolean) => void;
        setMeshAmountCents: (amountCents: number) => void;
        meshWithdrawalSourceSelected: (source: TransferSource) => void;
        setIsMeshMfaStep: (isMfaStep: boolean) => void;
        setMeshMethodSelectionDialogOpen: (open: boolean) => void;
        setMeshManualAddressEntryDialogOpen: (open: boolean) => void;
        endSession: () => void;
        isInitiated: () => boolean;
        isSelection: () => boolean;
        isPrereviewing: () => boolean;
        setBillingAddress: () => void;
        setSelection: () => void;
        setPrereviewing: () => void;
        setCard: () => void;
        setMfa: () => void;
        setInitiated: (opts?: { timedOut?: boolean; expectedArrivalTs?: string; transferId?: string }) => void;
        setFailed: (errorTitle?: string, errorMessage?: string) => void;
        setFundMethod: (method: FundMethods | undefined) => void;
        reset: () => void;
    };
}

/**
 * Every non-action field, as a fresh object.
 *
 * Used both to seed the store and to tear it down on logout, so the two cannot drift: a field
 * added here is cleared at session end automatically. That matters because this is a module-level
 * singleton shared by whoever is signed in next in the same tab, and the doc comment on
 * `WithdrawalDestination` above explains the stakes — two destination identifiers set at once
 * means money can leave for a destination other than the one on the review screen.
 */
function createInitialWithdrawalDialogState(): Omit<WithdrawalDialogState, 'actions'> {
    return {
        step: 'selection',
        fundMethod: undefined,
        lastInitiatedTime: DateTime.now(),
        loading: false,
        errorTitle: '',
        errorMessage: '',
        amount: 0,
        cardId: undefined,
        bankId: '',
        paymentId: undefined,
        cryptoWalletId: undefined,
        isStripeWithdrawal: false,
        stripeAccountLinked: false,
        stripeCardPayoutMethodId: '',
        isApplePayWithdrawal: false,
        braintreeType: undefined,
        initiatedTimedOut: false,
        initiatedExpectedArrivalTs: undefined,
        initiatedTransferId: undefined,
        lockedLimitsDialogOpen: false,
        lockedLimitsDialogTitle: '',
        lockedLimitsDialogLockedLimits: [],
        meshNetworkSelectionDialogOpen: false,
        meshAmountDialogOpen: false,
        meshAmountCents: 0,
        meshWithdrawalSource: 'event_contract',
        isMeshMfaStep: false,
        meshMethodSelectionDialogOpen: false,
        meshManualAddressEntryDialogOpen: false,
    };
}

export const useWithdrawalDialogStore: UseBoundStore<StoreApi<WithdrawalDialogState>> = create<WithdrawalDialogState>()(
    (set) => ({
        ...createInitialWithdrawalDialogState(),
        actions: {
            setAmount: (amount) => set({ amount }),
            selectDestination: (destination) => set(toDestinationSlice(destination)),
            setCardId: (cardId) => set({ cardId }),
            setBankId: (bankId) => set({ bankId }),
            setPaymentId: (paymentId) => set({ paymentId }),
            setCryptoWalletId: (cryptoWalletId) => set({ cryptoWalletId }),
            setIsStripeWithdrawal: (isStripeWithdrawal) => set({ isStripeWithdrawal }),
            setStripeAccountLinked: (stripeAccountLinked) => set({ stripeAccountLinked }),
            setStripeCardPayoutMethodId: (stripeCardPayoutMethodId) => set({ stripeCardPayoutMethodId }),
            setIsApplePayWithdrawal: (isApplePayWithdrawal) => set({ isApplePayWithdrawal }),
            setBraintreeType: (braintreeType) => set({ braintreeType }),
            setLockedLimitsDialogOpen: (lockedLimitsDialogOpen) => set({ lockedLimitsDialogOpen }),
            setLockedLimitsDialogTitle: (lockedLimitsDialogTitle) => set({ lockedLimitsDialogTitle }),
            setLockedLimitsDialogLockedLimits: (lockedLimitsDialogLockedLimits) =>
                set({ lockedLimitsDialogLockedLimits }),
            setMeshNetworkSelectionDialogOpen: (meshNetworkSelectionDialogOpen) =>
                set({ meshNetworkSelectionDialogOpen }),
            setMeshAmountDialogOpen: (meshAmountDialogOpen) => set({ meshAmountDialogOpen }),
            setMeshAmountCents: (meshAmountCents) => set({ meshAmountCents }),
            meshWithdrawalSourceSelected: (meshWithdrawalSource) => set({ meshWithdrawalSource }),
            setIsMeshMfaStep: (isMeshMfaStep) => set({ isMeshMfaStep }),
            setMeshMethodSelectionDialogOpen: (meshMethodSelectionDialogOpen) => set({ meshMethodSelectionDialogOpen }),
            setMeshManualAddressEntryDialogOpen: (meshManualAddressEntryDialogOpen) =>
                set({ meshManualAddressEntryDialogOpen }),
            endSession: () => set(createInitialWithdrawalDialogState()),
            isInitiated: () => useWithdrawalDialogStore.getState().step === 'initiated',
            isSelection: () => useWithdrawalDialogStore.getState().step === 'selection',
            isPrereviewing: () => useWithdrawalDialogStore.getState().step === 'prereview',
            setSelection: () => set({ step: 'selection' }),
            setPrereviewing: () => set({ step: 'prereview' }),
            setCard: () => set({ step: 'card' }),
            setMfa: () => set({ step: 'mfa' }),
            setInitiated: (opts?: { timedOut?: boolean; expectedArrivalTs?: string; transferId?: string }) => {
                set({
                    step: 'initiated',
                    lastInitiatedTime: DateTime.now(),
                    initiatedTimedOut: opts?.timedOut ?? false,
                    initiatedExpectedArrivalTs: opts?.expectedArrivalTs,
                    initiatedTransferId: opts?.transferId,
                });
                trackSprigEvent({ eventName: SPRIG_COMPLETED_WITHDRAWAL_FLOW_EVENT_NAME });
            },
            setFailed: (errorTitle?: string, errorMessage?: string) =>
                set({
                    // reset fields
                    amount: 0,
                    cardId: undefined,
                    bankId: '',
                    paymentId: undefined,
                    cryptoWalletId: undefined,
                    isStripeWithdrawal: false,
                    stripeCardPayoutMethodId: '',
                    braintreeType: undefined,
                    isApplePayWithdrawal: false,
                    fundMethod: undefined,
                    loading: false,
                    // failed state
                    step: 'failed',
                    errorTitle: errorTitle || 'Your withdrawal failed',
                    errorMessage: errorMessage || 'Review entered info and try again',
                }),
            setBillingAddress: () => set({ step: 'billing-address' }),
            setFundMethod: (fundMethod) =>
                set((state) => ({
                    fundMethod,
                    isStripeWithdrawal: fundMethod === FundMethods.DEBIT_CARD ? state.isStripeWithdrawal : false,
                    stripeCardPayoutMethodId:
                        fundMethod === FundMethods.DEBIT_CARD ? state.stripeCardPayoutMethodId : '',
                    isApplePayWithdrawal: fundMethod === FundMethods.DEBIT_CARD ? state.isApplePayWithdrawal : false,
                    braintreeType:
                        fundMethod === FundMethods.BRAINTREE_PAYPAL || fundMethod === FundMethods.BRAINTREE_VENMO
                            ? state.braintreeType
                            : undefined,
                })),
            reset: () =>
                set({
                    amount: 0,
                    cardId: undefined,
                    bankId: '',
                    paymentId: undefined,
                    cryptoWalletId: undefined,
                    isStripeWithdrawal: false,
                    stripeCardPayoutMethodId: '',
                    braintreeType: undefined,
                    isApplePayWithdrawal: false,
                    fundMethod: undefined,
                    loading: false,
                    errorTitle: '',
                    errorMessage: '',
                    meshWithdrawalSource: 'event_contract',
                }),
        },
    }),
);
